import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useTranslation } from 'react-i18next';
import { SimplePhoneInput } from '../common/SimplePhoneInput';
import { OTPInput } from '../common/OTPInput';
import { Logo } from '../common/Logo';
import { LanguageSwitcher } from '../common/LanguageSwitcher';
import apiService, { setTokenExpiry } from '../../services/api';
import { User } from '../../types/auth';
import toast from 'react-hot-toast';

// Схема валидации для входа
const loginSchema = z.object({
    identifier: z.string()
        .min(1, 'Введите номер телефона или email')
        .refine((value) => {
            const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
            // Обновленная регулярка для формата +7XXXXXXXXXX или +7 XXX XXX XXXX
            const phoneRegex = /^\+7\d{10}$|^\+7\s\d{3}\s\d{3}\s\d{4}$/;
            return emailRegex.test(value) || phoneRegex.test(value);
        }, 'Введите корректный номер телефона или email'),
});

type LoginFormData = z.infer<typeof loginSchema>;

interface OTPLoginFormProps {
    onSuccess?: (userData: User) => void;
}

type AuthStep = 'identifier' | 'otp';

export const OTPLoginForm: React.FC<OTPLoginFormProps> = ({ onSuccess }) => {
    const { t } = useTranslation(['auth', 'common']);
    const [currentStep, setCurrentStep] = useState<AuthStep>('identifier');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [identifier, setIdentifier] = useState('');
    const [otpCode, setOtpCode] = useState('');
    const [isPhone, setIsPhone] = useState(true);
    const [termsAccepted, setTermsAccepted] = useState(false);
    const [showTermsError, setShowTermsError] = useState(false);
    const [phoneInputValue, setPhoneInputValue] = useState('');

    const {
        register,
        handleSubmit,
        formState: { errors },
        setValue,
        watch,
        reset
    } = useForm<LoginFormData>({
        resolver: zodResolver(loginSchema)
    });

    const identifierValue = watch('identifier');

    // Определяем тип ввода (телефон или email)
    React.useEffect(() => {
        if (identifierValue) {
            const isPhoneInput = identifierValue.startsWith('+7') || identifierValue.startsWith('7');
            setIsPhone(isPhoneInput);
        }
    }, [identifierValue]);

    const handleContinue = async (data: LoginFormData) => {
        // Проверяем, приняты ли условия использования
        if (!termsAccepted) {
            setShowTermsError(true);
            toast.error(t('auth:terms.mustAcceptTerms'));
            return;
        }
        setShowTermsError(false);
        setIsSubmitting(true);
        try {
            // Отправляем OTP
            const response = await apiService.sendOTP({ identifier: data.identifier });

            if (response.data.success) {
                setIdentifier(data.identifier);
                setCurrentStep('otp');
                toast.success(t('auth:messages.codeSent'));
            } else {
                toast.error(response.data.message);
            }
        } catch (error: any) {
            console.error('Ошибка отправки OTP:', error);
            const message = error.response?.data?.message || 'Ошибка отправки кода';
            toast.error(message);
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleOTPVerify = async (otpCode: string) => {
        setIsSubmitting(true);
        try {
            const response = await apiService.verifyOTPLogin({
                identifier,
                otpCode
            });

            if (response.data.success && response.data.data) {
                console.log('OTP верификация успешна, сохраняем токены...');

                // Сохраняем токены
                const authData = response.data.data;
                localStorage.setItem('accessToken', authData.accessToken);
                localStorage.setItem('refreshToken', authData.refreshToken);
                // Сохраняем время истечения токена (24 часа)
                setTokenExpiry();

                console.log('Токены сохранены в localStorage');
                console.log('Access token:', authData.accessToken.substring(0, 20) + '...');
                console.log('Refresh token:', authData.refreshToken.substring(0, 20) + '...');

                // Обновляем состояние пользователя в AuthContext
                const userData = {
                    id: authData.userId,
                    login: authData.login,
                    fullName: authData.fullName,
                    role: authData.role,
                    status: authData.status,
                    companyId: authData.companyId,
                    isEmailVerified: true,
                    isPhoneVerified: true
                };

                console.log('Передаём данные пользователя в AuthContext:', userData);

                // Сохраняем пользователя в localStorage для быстрого восстановления при рефреше
                localStorage.setItem('user', JSON.stringify(userData));

                toast.success(t('auth:messages.loginSuccess'));
                onSuccess?.(userData);
            } else {
                toast.error(response.data.message || 'Ошибка входа');
            }
        } catch (error: any) {
            console.error('Ошибка верификации OTP:', error);
            const message = error.response?.data?.message || 'Неверный код';
            toast.error(message);
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleBack = () => {
        setCurrentStep('identifier');
        setIdentifier('');
    };

    if (currentStep === 'otp') {
        return (
            <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4">
                {/* Language Switcher в правом верхнем углу */}
                <div className="absolute top-4 right-4">
                    <LanguageSwitcher />
                </div>

                <div className="max-w-md w-full">
                    {/* Header */}
                    <div className="text-center mb-8">
                        <button
                            onClick={handleBack}
                            className="absolute left-6 top-6 p-2 text-gray-600 hover:text-gray-800"
                        >
                            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                            </svg>
                        </button>

                        <div className="w-16 h-16 bg-red-500 rounded-full flex items-center justify-center mx-auto mb-4 overflow-hidden p-2">
                            <Logo size="xl" className="w-full h-full rounded-full" />
                        </div>
                        <h1 className="text-2xl font-bold text-gray-900 mb-2">{t('auth:otp.title')}</h1>
                        <p className="text-gray-600">
                            {t('auth:otp.codeSentTo')} {isPhone ? t('auth:otp.codeSentToPhone') : t('auth:otp.codeSentToEmail')}: <br />
                            <span className="font-medium">{identifier}</span>
                        </p>
                    </div>

                    {/* OTP Input */}
                    <div className="space-y-6">
                        <OTPInput
                            length={4}
                            value={otpCode}
                            onChange={setOtpCode}
                            onComplete={handleOTPVerify}
                            disabled={isSubmitting}
                        />

                        <button
                            onClick={() => handleContinue({ identifier })}
                            disabled={isSubmitting}
                            className="w-full text-blue-600 hover:text-blue-800 text-sm font-medium"
                        >
                            {isSubmitting ? t('auth:login.sending') : t('auth:otp.resend')}
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4">
            {/* Language Switcher в правом верхнем углу */}
            <div className="absolute top-4 right-4">
                <LanguageSwitcher />
            </div>

            <div className="max-w-md w-full">
                {/* Header */}
                <div className="text-center mb-8">
                    <div className="w-16 h-16 bg-red-500 rounded-full flex items-center justify-center mx-auto mb-4 overflow-hidden p-2">
                        <Logo size="xl" className="w-full h-full rounded-full" />
                    </div>
                    <h1 className="text-2xl font-bold text-gray-900 mb-2">{t('common:appName')}</h1>
                    <p className="text-gray-600">{t('auth:login.title')}</p>
                </div>

                {/* Tab Switcher */}
                <div className="flex bg-gray-100 rounded-lg p-1 mb-6">
                    <button
                        type="button"
                        onClick={() => {
                            setIsPhone(true);
                            reset({ identifier: '' }); // Полностью очищаем форму при переключении
                            setPhoneInputValue(''); // Очищаем внутреннее состояние телефона
                        }}
                        className={`flex-1 py-2 px-4 text-sm font-medium rounded-md transition-colors ${isPhone
                            ? 'bg-white text-gray-900 shadow-sm'
                            : 'text-gray-600 hover:text-gray-900'
                            }`}
                    >
                        {t('auth:login.phone')}
                    </button>
                    <button
                        type="button"
                        onClick={() => {
                            setIsPhone(false);
                            reset({ identifier: '' }); // Полностью очищаем форму при переключении
                            setPhoneInputValue(''); // Очищаем внутреннее состояние телефона
                        }}
                        className={`flex-1 py-2 px-4 text-sm font-medium rounded-md transition-colors ${!isPhone
                            ? 'bg-white text-gray-900 shadow-sm'
                            : 'text-gray-600 hover:text-gray-900'
                            }`}
                    >
                        {t('auth:login.email')}
                    </button>
                </div>

                <form onSubmit={handleSubmit(handleContinue)} className="space-y-6">
                    {/* Input Field */}
                    <div className="flex space-x-3">
                        {isPhone && (
                            <div className="flex items-center bg-gray-100 px-3 py-3 rounded-lg">
                                <span className="text-lg mr-2">🇰🇿</span>
                                <span className="text-gray-700 font-medium">+7</span>
                            </div>
                        )}

                        <div className="flex-1">
                            {isPhone ? (
                                <SimplePhoneInput
                                    placeholder="747 630 2586"
                                    error={errors.identifier?.message}
                                    value={phoneInputValue}
                                    onChange={(e) => {
                                        const formattedValue = e.target.value;
                                        setPhoneInputValue(formattedValue);
                                        const phoneValue = formattedValue.replace(/\s/g, '');
                                        if (phoneValue.length === 10) {
                                            setValue('identifier', `+7${phoneValue}`);
                                        } else {
                                            setValue('identifier', `+7 ${formattedValue}`);
                                        }
                                    }}
                                    disabled={isSubmitting}
                                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-lg"
                                />
                            ) : (
                                <input
                                    {...register('identifier')}
                                    type="email"
                                    placeholder="example@mail.com"
                                    disabled={isSubmitting}
                                    className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-lg ${errors.identifier ? 'border-red-300' : 'border-gray-300'
                                        }`}
                                />
                            )}
                            {errors.identifier && (
                                <p className="text-red-500 text-sm mt-1">{errors.identifier.message}</p>
                            )}
                        </div>
                    </div>

                    {/* Continue Button */}
                    <button
                        type="submit"
                        disabled={isSubmitting}
                        className="w-full bg-blue-600 text-white py-3 px-4 rounded-lg font-medium text-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                        {isSubmitting ? t('auth:login.sending') : t('auth:login.button')}
                    </button>
                </form>

                {/* Terms Checkbox */}
                <div className="mt-8">
                    <label className={`flex items-start gap-3 cursor-pointer group ${showTermsError ? 'animate-shake' : ''}`}>
                        <div className="relative flex items-center justify-center mt-0.5">
                            <input
                                type="checkbox"
                                checked={termsAccepted}
                                onChange={(e) => {
                                    setTermsAccepted(e.target.checked);
                                    if (e.target.checked) {
                                        setShowTermsError(false);
                                    }
                                }}
                                className={`w-5 h-5 rounded border-2 transition-all cursor-pointer appearance-none ${
                                    termsAccepted
                                        ? 'bg-blue-600 border-blue-600'
                                        : showTermsError
                                            ? 'border-red-500 bg-red-50'
                                            : 'border-gray-300 bg-white group-hover:border-blue-400'
                                }`}
                            />
                            {termsAccepted && (
                                <svg
                                    className="absolute w-3 h-3 text-white pointer-events-none"
                                    fill="none"
                                    viewBox="0 0 24 24"
                                    stroke="currentColor"
                                    strokeWidth={3}
                                >
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                                </svg>
                            )}
                        </div>
                        <span className={`text-sm leading-relaxed ${showTermsError ? 'text-red-600' : 'text-gray-600'}`}>
                            {t('auth:terms.agreement')}{' '}
                            <a
                                href="/docs"
                                className="text-blue-600 hover:text-blue-800 underline font-medium"
                                onClick={(e) => e.stopPropagation()}
                            >
                                {t('auth:terms.readTerms')}
                            </a>
                        </span>
                    </label>
                    {showTermsError && (
                        <p className="text-red-500 text-xs mt-2 ml-8">{t('auth:terms.mustAcceptTerms')}</p>
                    )}
                </div>
            </div>
        </div>
    );
};
